import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, interval, of, timer } from 'rxjs';
import { catchError, startWith, switchMap, takeUntil, takeWhile } from 'rxjs/operators';
import { DepositStatusResponse, DepositConfirmationVm } from '../_core/_model/payments';
import { ApiService } from '../_core/_services/api.service';

type CallbackState = 'loading' | 'success' | 'pending' | 'failed';

@Component({
  selector: 'app-payment-callback',
  standalone: false,
  templateUrl: './payment-callback.html',
  styleUrls: ['./payment-callback.css'],
})
export class PaymentCallback implements OnInit, OnDestroy {
  state: CallbackState = 'loading';
  message = 'Verifying your payment...';
  result: DepositStatusResponse | null = null;

  txRef: string | null = null;
  transactionId: string | null = null;
  gatewayStatus: string | null = null;

  private destroy$ = new Subject<void>();
  private hasAutoRedirected = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private api: ApiService
  ) {}

  ngOnInit(): void {
    this.txRef = this.route.snapshot.queryParamMap.get('tx_ref');
    this.transactionId =
      this.route.snapshot.queryParamMap.get('transaction_id') ||
      this.route.snapshot.queryParamMap.get('transactionId');
    this.gatewayStatus = this.route.snapshot.queryParamMap.get('status');

    if (!this.txRef || !this.transactionId) {
      this.state = 'failed';
      this.message = 'Missing payment reference from gateway callback.';
      return;
    }

    this.verify();
  }

  get title(): string {
    switch (this.state) {
      case 'success':
        return 'Payment Confirmed';
      case 'pending':
        return 'Payment Processing';
      case 'failed':
        return 'Payment Verification Failed';
      default:
        return 'Verifying Payment';
    }
  }

  verify(): void {
    this.state = 'loading';
    this.message = 'Verifying your payment...';

    this.api.verifyFlutterwaveRedirect({
      txRef: this.txRef!,
      transactionId: this.transactionId!,
      status: this.gatewayStatus
    })
    .pipe(takeUntil(this.destroy$))
    .subscribe({
      next: (res) => {
        const data = res.data;
        console.log('Verification response:', res);

        if (!data) {
          this.state = 'pending';
          this.message = 'Verification response is empty. Checking payment status...';
          this.startPolling();
          return;
        }

        this.result = data;
        this.applyStateFromResponse(data);
      },
      error: (err) => {
        const backendMessage =
          err?.error?.message ||
          err?.error?.Message ||
          err?.error?.Error?.Details;

        this.state = 'pending';
        this.message = backendMessage || 'Verification is still in progress. Checking payment status...';
        this.startPolling();
      }
    });
  }

  startPolling(): void {
    if (!this.txRef) {
      this.state = 'failed';
      this.message = 'Payment reference is missing.';
      return;
    }

    interval(4000)
      .pipe(
        startWith(0),
        switchMap(() =>
          this.api.getDepositStatus(this.txRef!).pipe(
            catchError(() => of(null))
          )
        ),
        takeWhile((res, index) => {
          const data = res?.data ?? null;
          if (!data) return index < 9;
          return !this.isFinalState(data.status) && index < 9;
        }, true),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: (res) => {
          const data = res?.data ?? null;
          if (!data) return;

          this.result = data;
          this.applyStateFromResponse(data);
        },
        error: () => {
          this.state = 'failed';
          this.message = 'Unable to confirm payment status right now.';
        },
        complete: () => {
          if (!this.result || !this.isFinalState(this.result.status)) {
            this.state = 'pending';
            this.message = 'Your payment is still being processed. Please check again shortly.';
          }
        }
      });
  }

  private applyStateFromResponse(res: DepositStatusResponse): void {
    const status = (res.status || '').toLowerCase();

    if (status === 'confirmed' || status === 'completed' || status === 'success') {
      this.state = 'success';
      this.message = 'Your deposit has been confirmed successfully.';
      this.scheduleAutoRedirect();
      return;
    }

    if (status === 'pending') {
      this.state = 'pending';
      this.message = 'Your payment is pending confirmation.';
      return;
    }

    this.state = 'failed';
    this.message = `Payment status: ${res.status}`;
  }

  private scheduleAutoRedirect(): void {
    if (this.hasAutoRedirected || !this.result) return;

    this.hasAutoRedirected = true;

    timer(2000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.goToConfirmation();
      });
  }

  private buildConfirmationVm(res: DepositStatusResponse): DepositConfirmationVm {
    return {
      depositId: res.depositId,
      walletId: res.walletId ?? null,
      externalReference: res.externalReference,
      status: res.status,
      amount: res.amount,
      currencyCode: res.currencyCode,
      gatewayCode: res.gatewayCode,
      gatewayName: res.gatewayName,
      gatewayReference: res.gatewayReference,
      checkoutType: res.checkoutType,
      checkoutUrl: res.checkoutUrl,
      createdAt: res.createdAt,
      lastUpdatedAt: res.lastUpdatedAt,
      confirmedAt: res.confirmedAt
    };
  }

  private isFinalState(status: string | null | undefined): boolean {
    const value = (status || '').toLowerCase();
    return value === 'confirmed' || value === 'completed' || value === 'success' || value === 'failed';
  }

  goToConfirmation(): void {
    if (!this.result) {
      this.router.navigate(['/']);
      return;
    }

    this.router.navigate(
      ['/wallets/deposit/confirmation', this.result.depositId],
      {
        state: {
          depositConfirmation: this.buildConfirmationVm(this.result)
        }
      }
    );
  }

  retryVerification(): void {
    this.verify();
  }

  goHome(): void {
    this.router.navigate(['/']);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}